import { createContext, useContext, useEffect } from "react";

export interface BackLinkConfig {
  label: string;
  to: string;
}

export interface CanvasLayoutConfigType {
  setTitle: (title: string | undefined) => void;
  setSubtitle: (subtitle: string | undefined) => void;
  setCanvasParentPath: (path: string | undefined) => void;
  setBackLink: (backLink: BackLinkConfig | undefined) => void;
  setBackground: (background: string | undefined) => void;
  setDarkBackground: (isDarkBackground: boolean | undefined) => void;
}

export const CanvasLayoutContext = createContext<CanvasLayoutConfigType | undefined>(undefined);

export function useCanvasLayout() {
  const context = useContext(CanvasLayoutContext);
  if (context === undefined) {
    throw new Error("useLayoutConfig must be used within a LayoutConfigProvider");
  }
  return context;
}

// Custom hook for child pages to set the parent path in the layout
export function useCanvasParentPath(parentPath: string | undefined) {
  const { setCanvasParentPath } = useCanvasLayout();

  useEffect(() => {
    setCanvasParentPath(parentPath);
    // Clear the parent path when the component unmounts
    // or if the parentPath itself becomes undefined
    return () => {
      setCanvasParentPath(undefined);
    };
  }, [parentPath, setCanvasParentPath]);
}

export function useCanvasBackLink(label: string | undefined, to: string | undefined) {
  const { setBackLink } = useCanvasLayout();

  useEffect(() => {
    if (label && to) {
      setBackLink({ label, to });
    } else {
      setBackLink(undefined);
    }
    return () => {
      setBackLink(undefined);
    };
  }, [label, to, setBackLink]);
}

export function useCanvasTitle(title: string | undefined) {
  const { setTitle } = useCanvasLayout();

  useEffect(() => {
    setTitle(title);
    // Clear the parent path when the component unmounts
    // or if the parentPath itself becomes undefined
    return () => {
      setTitle(undefined);
    };
  }, [title, setTitle]);
}

export function useCanvasSubtitle(subtitle: string | undefined) {
  const { setSubtitle } = useCanvasLayout();

  useEffect(() => {
    setSubtitle(subtitle);
    // Clear the parent path when the component unmounts
    // or if the parentPath itself becomes undefined
    return () => {
      setSubtitle(undefined);
    };
  }, [subtitle, setSubtitle]);
}

export function useCanvasBackground(background: string | undefined) {
  const { setBackground } = useCanvasLayout();

  useEffect(() => {
    setBackground(background);
  }, [background, setBackground]);
}
